import type { TFunction } from 'i18next';
import type {
  CenterSafetyRepTask,
  NotificationCenter,
  NotificationSource,
} from '../../api/notificationCenter';
import type {
  HmsNotification,
  HmsNotificationType,
  SafetyRepTaskKind,
} from '../../api/safetyRepWork';
import type { DeviationSummary } from '../../api/safetyRounds';
import { Routes } from '../../enums/Routes';
import {
  notificationLink,
  notificationText,
  taskLink,
  taskTexts,
} from '../SafetyRep/safetyRepWorkView';
import {
  isRequirement,
  requirementView,
} from '../HRDashboard/HRWorkEnvironment/Overview/hrOverviewView';
import { deviationPath } from '../SafetyRounds/Deviations/deviationView';
import {
  managerProtocolPath,
  protocolPath,
} from '../SafetyRounds/Protocol/protocolView';
import { planPath } from '../SafetyRounds/Plans/plansView';
import { roundPath } from '../SafetyRounds/Round/roundView';
import { surveyPath } from '../WorkEnvironmentSurveyDetail/surveyRoutes';

/** «Venter på deg» is something to do, «Nytt siden sist» only tells what happened */
export type ItemGroup = 'waiting' | 'info' | 'read';

/** The role the user has it from, as the menu groups them */
export type ItemRole =
  | 'teamLeader'
  | 'safetyRep'
  | 'generalManager'
  | 'hr'
  | 'responsible';

export const ROLES: ItemRole[] = [
  'teamLeader',
  'safetyRep',
  'generalManager',
  'hr',
  'responsible',
];

/** What it is about, for its icon */
export type ItemIcon = 'document' | 'round' | 'survey' | 'measure';

export type CenterItem = {
  key: string;
  source: NotificationSource;
  /** One thing can be the user's from more than one role */
  roles: ItemRole[];
  icon: ItemIcon;
  group: ItemGroup;
  title: string;
  /** Team, plan or what it is about */
  meta: string;
  link: string;
  /** The deadline, or when it happened. A day or a time, as the module gives it. */
  date: string | null;
  overdue: boolean;
  /** False when the text already says the date */
  showDate: boolean;
  /** A notification from the safety representative's module, which is read when opened */
  notification: HmsNotification | null;
  /** The things one item stands for, when many of the same are shown as one */
  members?: string[];
  /** The survey it is about, so HR's follow-up is left out where the user has the task themselves */
  surveyId?: number;
};

/** Read notifications kept on the page, newest first */
export const READ_SHOWN = 20;

const K = 'notificationCenter.items';

const dayOf = (value: string) => value.slice(0, 10);

const isOverdue = (date: string | null, today: string) =>
  date !== null && dayOf(date) < today;

const todayIn = (now: Date) => {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

type Item = Pick<
  CenterItem,
  'key' | 'source' | 'roles' | 'title' | 'meta' | 'link' | 'date'
> &
  Partial<
    Pick<CenterItem, 'icon' | 'group' | 'overdue' | 'showDate' | 'surveyId'>
  > & {
    notification?: HmsNotification;
  };

const item = (value: Item, today: string): CenterItem => ({
  icon: 'document',
  group: 'waiting',
  showDate: true,
  ...value,
  notification: value.notification ?? null,
  overdue: value.overdue ?? isOverdue(value.date, today),
});

/** A short hash that is always the same for the same text (FNV-1a), so a key stays short whatever it stands for */
export const shortHash = (text: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

/**
 * The bell remembers keys, so a key must be short and the same in every language: built from ids and the kind, and
 * the keys of the areas, never their names. A task for other areas of the same survey is new again.
 */
export const centerTaskKey = (task: CenterSafetyRepTask) => {
  const areas = [...(task.areaKeys ?? [])].sort();
  const ref = task.surveyId ?? `${task.version?.subject ?? ''}${task.version?.id ?? ''}`;
  return [`hms-task-${task.kind}-${ref}`, ...(areas.length > 0 ? [shortHash(areas.join(','))] : [])].join('-');
};

/** The task a notification asks for, which takes its place in the list once it is there */
const TASK_OF: Partial<Record<HmsNotificationType, SafetyRepTaskKind>> = {
  DOCUMENTATION_TO_SIGN: 'sign',
  PLAN_REVIEW: 'reviewPlan',
  PLAN_REVIEW_REMINDER: 'reviewPlan',
  RISK_CALCULATED: 'assessRisk',
  RISK_REVIEW_REMINDER: 'assessRisk',
  VERSION_TO_APPROVE: 'approveVersion',
};

/** A version of the setup is not about a survey, so any version waiting stands for the notification */
const hasTaskFor = (notification: HmsNotification, tasks: CenterSafetyRepTask[]) => {
  const kind = TASK_OF[notification.type];
  return (
    kind !== undefined &&
    tasks.some((task) => task.kind === kind && (kind === 'approveVersion' || task.surveyId === notification.survey?.id))
  );
};

/** About a team the user no longer acts for, such as one they were removed from */
const isElsewhere = (notification: HmsNotification, teamIds: number[] | undefined) =>
  Boolean(notification.survey && teamIds && !teamIds.includes(notification.survey.team.id));

/** A notification about a team the user no longer acts for leads to their notifications, not to a page they cannot see */
const linkOf = (notification: HmsNotification, teamIds: number[] | undefined) => {
  const link = notificationLink(notification);
  const elsewhere = isElsewhere(notification, teamIds);
  return elsewhere && link !== Routes.SafetyRepSigning && link !== Routes.SafetyRepTeams
    ? Routes.Notifications
    : link;
};

const deviationItem = (
  deviation: DeviationSummary,
  action: 'check' | 'decide' | 'fix',
  role: ItemRole,
  t: TFunction,
  today: string,
): CenterItem =>
  item(
    {
      key: `deviation-${action}-${deviation.id}`,
      source: 'safetyRounds',
      roles: [role],
      icon: 'round',
      title: t(`${K}.${action}Deviation`, { deviation: deviation.title }),
      meta: `${deviation.number} · ${deviation.planName}`,
      link: deviationPath(deviation.id),
      date: deviation.dueDate,
      overdue: deviation.dueState === 'overdue',
    },
    today,
  );

const safetyRepItems = (
  part: NonNullable<NotificationCenter['safetyRep']>,
  t: TFunction,
  language: string,
  today: string,
) => [
  ...part.tasks.map((task) => {
    const texts = taskTexts(task, t, language);
    return item(
      {
        key: centerTaskKey(task),
        source: 'safetyRep',
        roles: ['safetyRep'],
        title: texts.title,
        // The module's own line says the team and the date
        meta: texts.meta,
        link: taskLink(task),
        date: task.due ?? null,
        showDate: false,
        surveyId: task.surveyId ?? undefined,
      },
      today,
    );
  }),
  /*
   * A notification about a task is shown as the task, which goes away when it is done. One with no such task in the
   * list, such as «For få svar», waits as itself until it is done or read. A task notification once read is not news,
   * and one from a team the user no longer acts for is not theirs to do (the server leaves those out as well).
   */
  ...part.notifications
    .filter((notification) =>
      notification.kind === 'task'
        ? notification.readAt === null &&
          !hasTaskFor(notification, part.tasks) &&
          !isElsewhere(notification, part.teamIds)
        : true,
    )
    .map((notification) =>
      item(
        {
          key: `hms-notification-${notification.id}`,
          source: 'safetyRep',
          roles: ['safetyRep'],
          group:
            notification.readAt !== null
              ? 'read'
              : notification.kind === 'info'
                ? 'info'
                : 'waiting',
          title: notificationText(notification, t),
          meta: notification.survey
            ? `${notification.survey.title} · ${notification.survey.team.name}`
            : '',
          link: linkOf(notification, part.teamIds),
          date: notification.createdAt,
          overdue: false,
          notification,
        },
        today,
      ),
    ),
];

const safetyRoundItems = (
  part: NonNullable<NotificationCenter['safetyRounds']>,
  t: TFunction,
  today: string,
) => {
  const { safetyRep, generalManager } = part.tasks;
  // Only a protocol sent back is the safety representative's to correct
  const sentBack = (safetyRep?.protocols ?? []).filter((round) => round.status === 'IN_PROGRESS');
  const correcting = new Set(sentBack.map((round) => round.id));
  return [
    // A round whose protocol was sent back is corrected, not walked again: it is shown once, as «Rett protokollen»
    ...(safetyRep?.roundsToDo ?? [])
      .filter((round) => round.roundId === null || !correcting.has(round.roundId))
      .map((round) =>
      item(
        {
          key: `round-todo-${round.planId}`,
          source: 'safetyRounds',
          roles: ['safetyRep'],
          icon: 'round',
          title: t(`${K}.roundToDo`, { plan: round.planName }),
          meta: round.area,
          link:
            round.roundId !== null
              ? roundPath(round.roundId)
              : planPath(round.planId),
          date: round.nextDate,
        },
        today,
      ),
    ),
    ...sentBack.map((round) =>
        item(
          {
            key: `protocol-correct-${round.id}`,
            source: 'safetyRounds',
            roles: ['safetyRep'],
            icon: 'round',
            title: t(`${K}.correctProtocol`, { plan: round.planName }),
            meta: '',
            link: protocolPath(round.id),
            date: null,
          },
          today,
        ),
      ),
    ...(safetyRep?.toCheck ?? []).map((deviation) =>
      deviationItem(deviation, 'check', 'safetyRep', t, today),
    ),
    ...(generalManager?.toSign ?? []).map((round) =>
      item(
        {
          key: `protocol-sign-${round.id}`,
          source: 'safetyRounds',
          roles: ['generalManager'],
          icon: 'round',
          title: t(`${K}.signProtocol`, { plan: round.planName }),
          meta: '',
          // The general manager signs in their own part of the app
          link: managerProtocolPath(round.id),
          date: null,
        },
        today,
      ),
    ),
    ...(generalManager?.toDecide ?? []).map((deviation) =>
      deviationItem(deviation, 'decide', 'generalManager', t, today),
    ),
    // The general manager's own deviations are among those the user is responsible for
    ...[...(generalManager?.responsible ?? []), ...part.deviations].map(
      (deviation) => deviationItem(deviation, 'fix', 'responsible', t, today),
    ),
  ];
};

/** A pulse survey is followed up when it is about to close with too few answers, not from its first minute */
export const PULSE_CLOSING_DAYS = 2;

/** HR reminds about a signature only when the safety representative has had a week */
export const SIGNATURE_WAIT_DAYS = 7;

/** A deviation in the documentation is news for two weeks, and then stays on the HR pages */
export const DEVIATION_NEWS_DAYS = 14;

const DAY_MS = 86_400_000;

const DEVIATION_KINDS = [
  'signatureDeviation',
  'assessmentDeviation',
  'approvalDeviation',
];

/** Many of the same thing as one item, which is new again when one more is added */
const grouped = (
  items: CenterItem[],
  group: (items: CenterItem[]) => CenterItem,
): CenterItem[] =>
  items.length > 1
    ? [
        {
          ...group(items),
          members: items.flatMap((value) => value.members ?? [value.key]),
        },
      ]
    : items;

const teamsText = (teams: string[], t: TFunction) => {
  const unique = [...new Set(teams)];
  return unique.length > 3
    ? t(`${K}.andMore`, {
        teams: unique.slice(0, 3).join(', '),
        count: unique.length - 3,
      })
    : unique.join(', ');
};

const hrItems = (
  part: NonNullable<NotificationCenter['hr']>,
  t: TFunction,
  language: string,
  today: string,
  now: Date,
  /** The surveys where the user has the task themselves, as team leader or safety representative */
  ownSurveys: Set<number>,
  /** The protocols the user can sign themselves, as general manager */
  ownProtocols: Set<number>,
) => {
  const requirements = part.followUp
    .filter(isRequirement)
    // HR's follow-up of a survey is left out where the user has the task themselves
    .filter(
      (followUp) =>
        !('surveyId' in followUp && ownSurveys.has(followUp.surveyId)),
    )
    .filter((followUp) => {
      if (followUp.kind === 'awaitingSignature')
        return followUp.days >= SIGNATURE_WAIT_DAYS;
      if (DEVIATION_KINDS.includes(followUp.kind) && 'at' in followUp)
        return (
          now.getTime() - new Date(followUp.at).getTime() <=
          DEVIATION_NEWS_DAYS * DAY_MS
        );
      return true;
    });
  const requirementItem = (followUp: (typeof requirements)[number]) => {
    const view = requirementView(followUp, t, language);
    return item(
      {
        key: `hr-${view.key}`,
        source: 'hr',
        roles: ['hr'],
        title: view.text,
        meta: view.team,
        link: view.link ?? Routes.HRWorkEnvironmentRequirementsFullPath,
        // An action plan past its deadline is the only one with a date, and its text says it
        date: followUp.kind === 'planOverdue' ? followUp.deadline : null,
        overdue: followUp.kind === 'planOverdue',
        showDate: false,
        surveyId: 'surveyId' in followUp ? followUp.surveyId : undefined,
      },
      today,
    );
  };
  const signatures = requirements.filter(
    (followUp) => followUp.kind === 'awaitingSignature',
  );
  // Measures belong to the team leader and the safety representative; HR follows up those past their deadline
  const overdue = part.measures.filter((measure) => measure.daysLeft < 0);
  const overdueMeasures = overdue.map((measure) =>
    item(
      {
        key: `measure-${measure.id}`,
        source: 'hr',
        roles: ['hr'],
        icon: 'measure',
        title: t(`${K}.measureOverdue`, { measure: measure.title }),
        meta: measure.area ? `${measure.team} · ${measure.area}` : measure.team,
        link: Routes.HRWorkEnvironmentMeasuresFullPath,
        date: measure.dueDate,
        overdue: true,
      },
      today,
    ),
  );
  // A protocol that has waited long for the general manager. One the user can sign is already theirs to do.
  const waitingProtocols = (part.protocolsWaiting ?? []).filter((protocol) => !ownProtocols.has(protocol.roundId));
  const protocolsWaiting = waitingProtocols.map((protocol) =>
    item(
      {
        key: `hr-protocolWaiting-${protocol.roundId}`,
        source: 'hr',
        roles: ['hr'],
        icon: 'round',
        title: t(`${K}.protocolWaitingForManager`, { plan: protocol.planName, count: protocol.days }),
        meta: '',
        link: Routes.HRSafetyRoundOverviewFullPath,
        date: null,
        overdue: true,
      },
      today,
    ),
  );
  const closingPulses = part.pulse.filter((survey) => {
    const left = new Date(survey.deadline).getTime() - now.getTime();
    return left >= 0 && left <= PULSE_CLOSING_DAYS * DAY_MS;
  });
  // A team without anyone who acts as safety representative: only HR can change that
  const withoutSafetyRep = part.followUp.flatMap((followUp) =>
    followUp.kind === 'noSafetyRep'
      ? [
          item(
            {
              key: `hr-noSafetyRep-${followUp.teamId}`,
              source: 'hr',
              roles: ['hr'],
              title: t(
                followUp.leaderIsSafetyRep
                  ? `${K}.leaderIsOnlySafetyRep`
                  : `${K}.noSafetyRep`,
                {
                  team: followUp.team,
                },
              ),
              meta: '',
              link: Routes.HRSafetyRepsFullPath,
              date: null,
            },
            today,
          ),
        ]
      : [],
  );
  const teamsWithout = part.followUp.flatMap((followUp) =>
    followUp.kind === 'noSafetyRep'
      ? [
          followUp.leaderIsSafetyRep
            ? t(`${K}.leaderIsRepTeam`, { team: followUp.team })
            : followUp.team,
        ]
      : [],
  );
  return [
    ...requirements
      .filter((followUp) => followUp.kind !== 'awaitingSignature')
      .map(requirementItem),
    ...grouped(withoutSafetyRep, (items) =>
      item(
        {
          key: 'hr-noSafetyRep',
          source: 'hr',
          roles: ['hr'],
          title: t(`${K}.teamsWithoutSafetyRep`, { count: items.length }),
          meta: teamsText(teamsWithout, t),
          link: Routes.HRSafetyRepsFullPath,
          date: null,
        },
        today,
      ),
    ),
    ...grouped(signatures.map(requirementItem), (items) =>
      item(
        {
          key: 'hr-awaitingSignature',
          source: 'hr',
          roles: ['hr'],
          title: t(`${K}.signaturesWaiting`, { count: items.length }),
          meta: teamsText(
            items.map((value) => value.meta),
            t,
          ),
          link: Routes.HRWorkEnvironmentRequirementsFullPath,
          date: null,
        },
        today,
      ),
    ),
    ...grouped(overdueMeasures, (items) =>
      item(
        {
          key: 'measures-overdue',
          source: 'hr',
          roles: ['hr'],
          icon: 'measure',
          title: t(`${K}.measuresOverdue`, { count: items.length }),
          meta: teamsText(
            overdue.map((measure) => measure.team),
            t,
          ),
          link: Routes.HRWorkEnvironmentMeasuresFullPath,
          // The oldest deadline says how long it has waited
          date: overdue.map((measure) => measure.dueDate).sort()[0],
          overdue: true,
        },
        today,
      ),
    ),
    ...grouped(protocolsWaiting, (items) =>
      item(
        {
          key: 'hr-protocolsWaiting',
          source: 'hr',
          roles: ['hr'],
          icon: 'round',
          title: t(`${K}.protocolsWaitingForManager`, { count: items.length }),
          meta: teamsText(
            waitingProtocols.map((protocol) => protocol.planName),
            t,
          ),
          link: Routes.HRSafetyRoundOverviewFullPath,
          date: null,
          overdue: true,
        },
        today,
      ),
    ),
    ...closingPulses.map((survey) =>
      item(
        {
          key: `pulse-${survey.id}`,
          source: 'hr',
          roles: ['hr'],
          icon: 'survey',
          title: t(`${K}.lowResponse`, { survey: survey.title }),
          meta: t(`${K}.responses`, {
            team: survey.team,
            responses: survey.responses,
            expected: survey.expected,
          }),
          link: Routes.HRHomeFullPath,
          date: survey.deadline,
        },
        today,
      ),
    ),
  ];
};

/** The surveys where the user has the task themselves, so HR's follow-up of them is not shown twice */
const ownSurveysOf = (center: NotificationCenter) =>
  new Set([
    ...(center.teamLeader ?? []).map((task) => task.surveyId),
    ...(center.safetyRep?.tasks ?? []).flatMap((task) =>
      task.surveyId !== null ? [task.surveyId] : [],
    ),
  ]);

/** The protocols the user signs themselves as general manager, so HR's follow-up of them is not shown twice */
const ownProtocolsOf = (center: NotificationCenter) =>
  new Set((center.safetyRounds?.tasks.generalManager?.toSign ?? []).map((round) => round.id));

const TEAM_TASK_TEXTS: Record<
  NonNullable<NotificationCenter['teamLeader']>[number]['kind'],
  string
> = {
  plan: 'makePlan',
  review: 'reviewMeasures',
  askSignature: 'askSignature',
  registerNoSignature: 'registerNoSignature',
  signatureReturned: 'signatureReturned',
};

const teamLeaderItems = (
  part: NonNullable<NotificationCenter['teamLeader']>,
  t: TFunction,
  today: string,
) =>
  part.map((task) =>
    item(
      {
        key: `team-${task.kind}-${task.surveyId}`,
        source: 'teamLeader',
        roles: ['teamLeader'],
        title: t(`${K}.${TEAM_TASK_TEXTS[task.kind]}`, { survey: task.title }),
        meta: task.team,
        link: surveyPath('team', task.surveyId),
        date: task.date,
        surveyId: task.surveyId,
      },
      today,
    ),
  );

/** A notification has a time it came, not a deadline */
const deadlineOf = (value: CenterItem) =>
  value.notification ? null : value.date;

const byDeadline = (a: CenterItem, b: CenterItem) =>
  Number(b.overdue) - Number(a.overdue) ||
  (deadlineOf(a) === null ? 1 : 0) - (deadlineOf(b) === null ? 1 : 0) ||
  (deadlineOf(a) ?? '').localeCompare(deadlineOf(b) ?? '');

const newestFirst = (a: CenterItem, b: CenterItem) =>
  (b.date ?? '').localeCompare(a.date ?? '');

/**
 * Every module's part as one list: what waits, the overdue first and then by deadline, what is new, newest first, and
 * the latest of what is read. The same thing from two roles is shown once, with both roles.
 */
export const centerItems = (
  center: NotificationCenter,
  t: TFunction,
  language: string,
  now = new Date(),
) => {
  const today = todayIn(now);
  const all = [
    ...(center.safetyRep
      ? safetyRepItems(center.safetyRep, t, language, today)
      : []),
    ...(center.safetyRounds
      ? safetyRoundItems(center.safetyRounds, t, today)
      : []),
    ...(center.hr
      ? hrItems(center.hr, t, language, today, now, ownSurveysOf(center), ownProtocolsOf(center))
      : []),
    ...(center.teamLeader ? teamLeaderItems(center.teamLeader, t, today) : []),
  ];
  const unique = all.flatMap((value, index) => {
    if (all.findIndex((other) => other.key === value.key) !== index) return [];
    const roles = all
      .filter((other) => other.key === value.key)
      .flatMap((other) => other.roles);
    return [{ ...value, roles: ROLES.filter((role) => roles.includes(role)) }];
  });
  return {
    waiting: unique
      .filter((value) => value.group === 'waiting')
      .sort(byDeadline),
    info: unique.filter((value) => value.group === 'info').sort(newestFirst),
    read: unique
      .filter((value) => value.group === 'read')
      .sort(newestFirst)
      .slice(0, READ_SHOWN),
  };
};

export type CenterItems = ReturnType<typeof centerItems>;

const keysOf = (value: CenterItem) => value.members ?? [value.key];

/**
 * What the bell knows about: what waits and what is not read yet, and what a grouped item stands for. While a part
 * could not be fetched, what was seen before is kept, so its items are not new again when it is back.
 */
export const seenKeys = (items: CenterItems, center?: Pick<NotificationCenter, 'seen' | 'unavailable'>) => {
  const now = [...items.waiting, ...items.info].flatMap(keysOf);
  const kept = center && center.unavailable.length > 0 ? center.seen.filter((key) => !now.includes(key)) : [];
  return [...now, ...kept];
};

/**
 * The bell counts only what is new since the user last opened it, as in other apps. A grouped item is new when one more
 * thing is added to it, not when one leaves.
 */
export const badgeCount = (items: CenterItems, seen: string[]) => {
  const known = new Set(seen);
  return [...items.waiting, ...items.info].filter((value) =>
    keysOf(value).some((key) => !known.has(key)),
  ).length;
};

export const onlyRole = (
  items: CenterItems,
  role: ItemRole | 'all',
): CenterItems =>
  role === 'all'
    ? items
    : {
        waiting: items.waiting.filter((value) => value.roles.includes(role)),
        info: items.info.filter((value) => value.roles.includes(role)),
        read: items.read.filter((value) => value.roles.includes(role)),
      };

/** The roles with something in them, for the filter */
export const rolesIn = (items: CenterItems) =>
  ROLES.filter((role) =>
    [...items.waiting, ...items.info, ...items.read].some((value) =>
      value.roles.includes(role),
    ),
  );

/**
 * Where each kind of email is chosen, for the roles the user has. Anyone can be responsible for a deviation, and HR
 * gets the emails about rounds past their date, so everyone with safety rounds chooses theirs: the general manager on
 * their own page, which also has the deviations they are responsible for, and the others on the safety rounds' page.
 */
export const settingsLinks = (
  center: NotificationCenter,
): { key: 'safetyRep' | 'safetyRounds' | 'generalManager'; to: string }[] => {
  const rounds = center.safetyRounds?.tasks;
  const manager = Boolean(rounds?.generalManager);
  return [
    ...(center.safetyRep ? [{ key: 'safetyRep' as const, to: Routes.SafetyRepOverview }] : []),
    ...(center.safetyRounds && (rounds?.safetyRep || !manager)
      ? [{ key: 'safetyRounds' as const, to: Routes.SafetyRoundNotifications }]
      : []),
    ...(manager ? [{ key: 'generalManager' as const, to: Routes.GeneralManagerNotifications }] : []),
  ];
};

/** «I dag kl. 09:12», «I går kl. 14:05», or the date */
export const relativeTime = (
  value: string,
  t: TFunction,
  language: string,
  now = new Date(),
) => {
  const locale = language === 'en' ? 'en-GB' : 'nb-NO';
  const date = new Date(value);
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  const day = todayIn(date);
  const today = todayIn(now);
  const yesterday = todayIn(new Date(now.getTime() - 86_400_000));
  if (day === today) return t('notificationCenter.today', { time });
  if (day === yesterday) return t('notificationCenter.yesterday', { time });
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  return t('notificationCenter.onDay', {
    date: new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' }),
    }).format(date),
    time,
  });
};

/** «Forfalt 30. september» or «Frist 6. oktober» */
export const deadlineText = (
  value: CenterItem,
  t: TFunction,
  language: string,
) => {
  if (!value.date || !value.showDate) return null;
  const locale = language === 'en' ? 'en-GB' : 'nb-NO';
  // A day without time is a calendar day, and must not move with the time zone
  const date =
    value.date.length === 10
      ? new Date(`${value.date}T12:00:00`)
      : new Date(value.date);
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
  }).format(date);
  return t(
    value.overdue
      ? 'notificationCenter.overdueSince'
      : 'notificationCenter.due',
    { date: day },
  );
};
